import React, { useState } from 'react';
import { CloseIcon } from './icons';

export const WorkspacesPanel = ({ 
  workspaces = [], 
  currentWorkspaceId, 
  onSwitchWorkspace, 
  onCreateWorkspace, 
  onDeleteWorkspace,
  onClose,
  onImportToNewWorkspace,
  onShowAlert
}) => {
  const [showNewForm, setShowNewForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importName, setImportName] = useState('');

  const handleCreateWorkspace = () => {
    if (newWorkspaceName.trim()) {
      onCreateWorkspace(newWorkspaceName.trim());
      setNewWorkspaceName('');
      setShowNewForm(false);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
      if (!importName) {
        setImportName(file.name.replace('.json', ''));
      }
    }
  };

  const handleImportWorkspace = async () => {
    if (!importFile || !importName.trim()) {
      onShowAlert && onShowAlert('Import Error', 'Please select a file and enter a workspace name.');
      return;
    }

    try {
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error('Failed to read file.'));
        reader.readAsText(importFile);
      });
      
      const data = JSON.parse(content);
      
      // Validate the structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid file format. Expected a JSON object.');
      }
      
      // Check if it's a workflow file or a general JSON
      let workflowData;
      if (Array.isArray(data.nodes) && Array.isArray(data.edges)) {
        // It's a workflow file
        workflowData = data;
      } else {
        // Try to parse as a general workflow object
        workflowData = data.workflow || data;
        if (!Array.isArray(workflowData.nodes) || !Array.isArray(workflowData.edges)) {
          throw new Error('Invalid workflow file structure. Missing nodes or edges.');
        }
      }
      
      onImportToNewWorkspace(importName.trim(), workflowData);
      setImportFile(null);
      setImportName('');
    } catch (err) {
      console.error("Failed to import workspace:", err);
      onShowAlert && onShowAlert('Import Error', `Failed to import workspace: ${err.message}`);
    }
  };

  return (
    <div className="absolute top-16 left-4 bg-dark-surface border border-dark-border rounded-lg shadow-xl z-40 w-80">
      <div className="p-4 border-b border-dark-border flex items-center justify-between">
        <h3 className="font-bold text-lg text-dark-text-primary">Workspaces</h3>
        <button 
          onClick={onClose}
          className="p-1 rounded-md text-dark-text-secondary hover:bg-dark-bg"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>
      
      <div className="p-4 max-h-96 overflow-y-auto">
        <div className="space-y-2">
          {workspaces.map(workspace => (
            <div 
              key={workspace.id}
              className={`flex items-center justify-between p-3 rounded-md ${
                currentWorkspaceId === workspace.id 
                  ? 'bg-brand-primary/20 border border-brand-primary/30' 
                  : 'bg-dark-bg hover:bg-dark-surface'
              }`}
            >
              <button
                onClick={() => onSwitchWorkspace(workspace.id)}
                className="flex-1 text-left text-dark-text-primary truncate"
              >
                <div className="font-medium">{workspace.name}</div>
                <div className="text-xs text-dark-text-secondary">
                  {new Date(workspace.lastModified).toLocaleDateString()}
                </div>
              </button>
              
              {workspaces.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Are you sure you want to delete this workspace?')) {
                      onDeleteWorkspace(workspace.id);
                    }
                  }}
                  className="ml-2 p-1 text-red-400 hover:text-red-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-6 space-y-4">
          {showNewForm ? (
            <div className="space-y-3">
              <h4 className="font-medium text-dark-text-primary">Create New Workspace</h4>
              <input
                type="text"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="Workspace name"
                className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-2 text-sm text-dark-text-primary"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreateWorkspace}
                  className="flex-1 bg-brand-primary text-white rounded-md px-3 py-2 text-sm"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewForm(false);
                    setNewWorkspaceName('');
                  }}
                  className="flex-1 bg-dark-bg border border-dark-border text-dark-text-primary rounded-md px-3 py-2 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowNewForm(true)}
              className="w-full flex items-center gap-2 p-3 bg-dark-bg hover:bg-dark-surface border border-dark-border rounded-md text-dark-text-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Workspace
            </button>
          )}
          
          <div className="border-t border-dark-border pt-4">
            <h4 className="font-medium text-dark-text-primary mb-3">Import Workspace</h4>
            <div className="space-y-3">
              <input
                type="text"
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="Workspace name"
                className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-2 text-sm text-dark-text-primary"
              />
              <div className="flex gap-2">
                <label className="flex-1 flex items-center justify-center gap-2 p-2 bg-dark-bg hover:bg-dark-surface border border-dark-border rounded-md text-dark-text-primary cursor-pointer">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-sm">Select File</span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportFile}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={handleImportWorkspace}
                  disabled={!importFile}
                  className="flex-1 bg-brand-primary text-white rounded-md px-3 py-2 text-sm disabled:opacity-50"
                >
                  Import
                </button>
              </div>
              {importFile && (
                <div className="text-xs text-dark-text-secondary truncate">
                  Selected: {importFile.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};